/**
 * Projetista do executivo de cada ambiente do Legado — quem está com o
 * ambiente e desde quando. A planilha não guarda isso: vem do backend, ligado
 * pela chave "PEDIDO:AMBIENTE" (`chaveAmbiente`).
 *
 * Uma request para a página inteira (`/executivo/projetistas`), num cache de
 * módulo compartilhado pela tabela, pelo painel do ambiente e pelos
 * Relatórios. Quem atribui recebe de volta só os ambientes alterados e o
 * cache é atualizado no lugar, sem reler tudo.
 */
import { useCallback, useEffect, useState } from "react";

import { useAuth } from "../context/AuthContext";
import { atribuirProjetistaExecutivo, lerProjetistasExecutivo } from "../services/mapaProducao";
import { PERMS_HISTORICO_AMBIENTE } from "./useContagemComentarios";

/** Mesmo gate de leitura do backend (`views_projetistas.PERMS_LER`). */
export const PERMS_LER_PROJETISTA = [...PERMS_HISTORICO_AMBIENTE, "relatorios_legado:view"];
export const PERM_ATRIBUIR_PROJETISTA = "executivo:atribuir_projetista";
export const PERM_SER_PROJETISTA = "executivo:ser_projetista";

const VALIDADE_MS = 60_000;
const VAZIO = Object.freeze({
  atribuicoes: Object.freeze({}),
  projetistas: Object.freeze([]),
  podeAtribuir: false,
  carregado: false,
});

const cache = { dono: null, estado: VAZIO, lidoEm: 0, emAndamento: null, request: null };
const ouvintes = new Set();

function publicar(estado) {
  cache.estado = estado;
  ouvintes.forEach((ouvinte) => ouvinte(estado));
}

function buscar(request, dono) {
  if (cache.emAndamento && cache.dono === dono) return cache.emAndamento;
  cache.dono = dono;
  cache.request = request;
  cache.emAndamento = lerProjetistasExecutivo(request)
    .then((resposta) => {
      if (cache.dono !== dono) return;
      cache.lidoEm = Date.now();
      publicar({
        atribuicoes: resposta?.atribuicoes || {},
        projetistas: Array.isArray(resposta?.projetistas) ? resposta.projetistas : [],
        podeAtribuir: Boolean(resposta?.pode_atribuir),
        carregado: true,
      });
    })
    // Sem a resposta as colunas ficam vazias e somente leitura; a próxima
    // tentativa só depois da validade (não repete em loop).
    .catch(() => {
      cache.lidoEm = Date.now();
    })
    .finally(() => {
      cache.emAndamento = null;
    });
  return cache.emAndamento;
}

/**
 * Releitura imediata: depois de religar um ambiente renomeado, a atribuição
 * passa a morar na chave nova e a lista em memória ainda está na velha.
 */
export function recarregarProjetistasExecutivo() {
  if (!cache.request) return Promise.resolve();
  cache.lidoEm = 0;
  return buscar(cache.request, cache.dono);
}

/** Só para testes: esquece o que foi lido. */
export function limparCacheDeProjetistas() {
  cache.dono = null;
  cache.estado = VAZIO;
  cache.lidoEm = 0;
  cache.emAndamento = null;
  cache.request = null;
}

/**
 * `{ atribuicoes, projetistas, podeAtribuir, carregado, atribuir }`.
 * `atribuir(itens, projetistaId, inicio)`: `itens` = [{chave, rotulo}];
 * `projetistaId` nulo retira; `inicio` "AAAA-MM-DD" (vazio = hoje). Rejeita
 * com a mensagem do backend em `err.message`.
 */
export default function useProjetistasExecutivo({ ativo = true } = {}) {
  const { request, usuario, hasAnyPermission } = useAuth() || {};
  const permitido =
    ativo &&
    Boolean(usuario) &&
    typeof hasAnyPermission === "function" &&
    hasAnyPermission(PERMS_LER_PROJETISTA);
  const dono = usuario?.id ?? null;
  const [estado, setEstado] = useState(() => (cache.dono === dono ? cache.estado : VAZIO));

  useEffect(() => {
    if (!permitido) return undefined;
    ouvintes.add(setEstado);
    if (cache.dono !== dono) {
      cache.estado = VAZIO;
      cache.lidoEm = 0;
    }
    setEstado(cache.estado);
    if (Date.now() - cache.lidoEm > VALIDADE_MS) buscar(request, dono);
    return () => {
      ouvintes.delete(setEstado);
    };
  }, [permitido, request, dono]);

  const atribuir = useCallback(
    async (itens, projetistaId, inicio) => {
      const resposta = await atribuirProjetistaExecutivo(request, { itens, projetistaId, inicio });
      if (cache.dono === dono) {
        publicar({
          ...cache.estado,
          atribuicoes: { ...cache.estado.atribuicoes, ...(resposta?.atribuicoes || {}) },
        });
      }
      return resposta;
    },
    [request, dono],
  );

  return permitido ? { ...estado, atribuir } : { ...VAZIO, atribuir };
}
